import React, { useEffect, useState } from "react";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { groq } from "next-sanity";
import { ArticleJsonLd } from "next-seo";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import { Box, Text, Flex, AspectRatio, SimpleGrid } from "@chakra-ui/react";
import * as Sentry from "@sentry/nextjs";
import { getPageMetaImage } from "utils";
import formatDate from "date-fns/format";
import readingTime from "reading-time";
import { trackEvent } from "lib/analytics";
import {
  getClient,
  urlFor,
  usePreviewSubscription,
  getBlogs,
  type Blog,
  type BlogSimple,
  getBlogPost,
  getDefaultBlogs,
  getAllEmbeddedWhitepapers,
  type EmbeddedWhitepaper,
  getAnnouncementBanner,
  type Banner,
} from "lib/sanity";
import NotFound from "pages/404";
import { components } from "components/mdx/components";
import { Page } from "components/page";
import { Article, ArticleContainer } from "components/article";
import { type SidebarHeading, Sidebar } from "components/sidebar";
import { ResourceCard } from "components/resource-card";
import { getArticleHeadings, serializeArticle } from "components/mdx/utils";
import { IndustryLeaderAwards } from "components/content";
import { Section, sectionSpacingY } from "components/section";
import { redirectedBlogs } from "../../constants/redirected-blogs";
import { blogBlurDataUrl } from "../../constants/blur-data-urls";

type PageProps = {
  post: Blog;
  slug: string;
  preview?: boolean;
  mdx: MDXRemoteSerializeResult;
  readingTime: number;
  headings: SidebarHeading[];
  relatedBlogs: BlogSimple[];
  allEmbeddedWhitepapers: EmbeddedWhitepaper[];
  announcementBanner?: Banner;
};

const postQuery = groq`
  *[_type == "blog" && slug == $slug][0] {
    title,
    content,
    excerpt,
    slug,
    image,
    imageAlt,
    publishedAt,
    authors[]->
  }
`;

const BlogPost: NextPage<Readonly<PageProps>> = ({
  post,
  slug,
  preview,
  mdx,
  readingTime,
  headings,
  relatedBlogs,
  allEmbeddedWhitepapers,
  announcementBanner,
}) => {
  const router = useRouter();
  const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult>(mdx);
  const metaTitle = post.metaTitle ?? post.title;
  const metaDescription = post.metaDescription ?? post.excerpt;
  const metaImageUrl = post.metaImageUrl ? `${post.metaImageUrl}?w=1200` : urlFor(post.image).width(1200).url();

  const { data: previewPost } = usePreviewSubscription(postQuery, {
    params: { slug },
    initialData: post,
    enabled: Boolean(preview && slug),
  });

  useEffect(() => {
    if (post) {
      trackEvent("Blog Post Viewed", {
        post_name: post.title,

        post_author: post.authors[0].name,

        post_published: post.publishedAt,

        post_slug: post.slug,

        persona_tag: post.personaTag,
      });
    }
  }, [post]);

  const serializeMdx = async (content: string) => {
    const newMdx = await serializeArticle(content);
    setMdxSource(newMdx);
  };

  useEffect(() => {
    if (previewPost) {
      serializeMdx(previewPost.content).catch((error) => {
        Sentry.captureException(error);
      });
    }
  }, [previewPost]);

  if (!router.isFallback && (!slug || !post)) {
    return <NotFound />;
  }

  if (!post || !mdxSource) {
    return null;
  }

  return (
    <Page
      hasSmoothScroll
      title={metaTitle}
      description={metaDescription}
      image={getPageMetaImage({ src: metaImageUrl, alt: metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section as="article" my={0} mt={{ base: 12, md: 16 }}>
        <Flex gap={{ base: "bigger", md: "biggest" }} direction={{ base: "column", lg: "row" }} align="start">
          <Flex direction="column" gap="bigger" flex={1}>
            <Text as="h1" textStyle={{ base: "article.h2", md: "article.h1" }} mb={{ base: 0, md: 0 }}>
              {post.title}
            </Text>

            {post.excerpt && (
              <Text color="brand.textSecondary.light" textStyle="article.p" fontSize="brand.lg" mb={0}>
                {post.excerpt}
              </Text>
            )}

            <ArticleJsonLd
              type="Blog"
              url={`https://hightouch.com/blog/${post.slug}`}
              title={metaTitle}
              images={[urlFor(post.image).width(200).height(200).fit("crop").crop("center").url()]}
              datePublished={post.publishedAt}
              dateModified={post.publishedAt}
              authorName={post.authors[0].name}
              description={metaDescription}
            />

            <Flex color="brand.textSecondary.light" gap="tiny" textStyle="caption1" wrap="wrap">
              <Flex align="center" gap="tiny">
                {post.authors?.map(({ name }, index) => (
                  <Text key={name}>
                    {name}
                    {post.authors?.length > 1 && index < post.authors.length - 1 && ","}
                  </Text>
                ))}
              </Flex>{" "}
              |<Text>{formatDate(new Date(post.publishedAt), "MMMM d, yyyy")}</Text> |<Text>{readingTime} minutes</Text>
            </Flex>
          </Flex>

          <Box width="100%" flex={1} height="auto">
            <Box
              pos="relative"
              width="100%"
              borderRadius="base"
              border="1px"
              borderColor="new.gray.150"
              overflow="hidden"
            >
              <Image
                src={urlFor(post.image).width(2000).url()}
                alt={post.imageAlt ?? post.title + "."}
                placeholder="blur"
                blurDataURL={blogBlurDataUrl}
                sizes="100%"
                width={600}
                height={300}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "auto",
                }}
              />
            </Box>
          </Box>
        </Flex>

        <ArticleContainer mt={[16, 16, 24]}>
          <Article>
            <MDXRemote
              lazy
              components={components({ publishedAt: post.publishedAt, type: "blog", allEmbeddedWhitepapers })}
              {...mdxSource}
            />

            <Text as="h2" textStyle={{ base: "article.h3", md: "article.h2" }} pt={{ base: "big", lg: "bigger" }}>
              More on the blog
            </Text>

            <SimpleGrid
              as="ul"
              mt={{ base: 8, md: 12 }}
              columns={{ base: 1, sm: 2, lg: 3 }}
              width="100%"
              gap={{ base: 5, sm: 3, xl: 5 }}
            >
              {relatedBlogs.map((resource) => (
                <Flex key={resource.slug} as="li" listStyleType="none" height="100%" width="100%">
                  <ResourceCard
                    href={`/blog/${resource.slug}`}
                    linkAriaLabel={`Read ${resource.title}.`}
                    title={resource.title}
                    titleElement="h3"
                    excerpt={resource.excerpt}
                    actionLabel="Read"
                    image={
                      <AspectRatio ratio={2 / 1} width="100%">
                        <Image
                          fill
                          src={resource.image ? urlFor(resource.image).width(600).url() : "/assets/meta.png"}
                          alt={resource.title + "."}
                          sizes="100%"
                          placeholder="blur"
                          blurDataURL={blogBlurDataUrl}
                          style={{ objectFit: "cover" }}
                        />
                      </AspectRatio>
                    }
                  />
                </Flex>
              ))}
            </SimpleGrid>
          </Article>

          <Sidebar title="In this article" headings={headings} path={`blog/${slug}`} />
        </ArticleContainer>
      </Section>

      <IndustryLeaderAwards hideBg my={0} py={sectionSpacingY} />
    </Page>
  );
};

const getReadingTime = (blogContent: string) => Math.ceil(readingTime(blogContent).minutes);

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview = false }) => {
  const slug = params?.slug ? String(params?.slug) : "";
  const [post, announcementBanner] = await Promise.all([getBlogPost(slug), getAnnouncementBanner()]);

  if (!post) {
    return {
      notFound: true,
    };
  }

  const mdx = await serializeArticle(post.content);
  const relatedSlugs = post.relatedBlogs?.filter(Boolean)?.slice(0, 3);
  const relatedBlogs = relatedSlugs && relatedSlugs.length > 0 ? await getBlogs(relatedSlugs) : await getDefaultBlogs();
  const allEmbeddedWhitepapers = await getAllEmbeddedWhitepapers();

  return {
    props: {
      preview,
      slug,
      post,
      mdx,
      readingTime: getReadingTime(post.content),
      headings: getArticleHeadings(post.content),
      relatedBlogs,
      allEmbeddedWhitepapers,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(
    groq`*[_type == "blog" && !hidden && !(slug in [${redirectedBlogs.map((slug) => "'" + slug + "'")}]) ] { slug }`,
  );

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default BlogPost;
